import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, LogIn, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { AuthHero, KlysticLogo } from "@/components/KlysticBrand";

function formatErr(err) {
  // Network / preflight failure — no response at all
  if (err && !err.response) {
    return `Network error — cannot reach ${API}. Check that the backend URL is correct and DNS is live.`;
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || code.length !== 6) return;
    setBusy(true);
    try {
      await signIn({ email, password, code });
      const to = loc.state?.from || "/";
      nav(to, { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      <AuthHero
        overline="Secure sign-in"
        title="Sign in with"
        highlight="password + 2FA."
        description={
          <>
            Every Klystic session is protected by an authenticator app (Google
            Authenticator or Microsoft Authenticator). You&apos;ll enter your password
            and a 6-digit code each time you sign in.
          </>
        }
      >
        <ul className="space-y-3">
          <li className="flex items-center gap-2">
            <KeyRound size={14} className="text-klystic-green" /> bcrypt-hashed passwords
          </li>
          <li className="flex items-center gap-2">
            <Smartphone size={14} className="text-klystic-green" /> RFC 6238 TOTP 2FA
          </li>
        </ul>
      </AuthHero>

      <div className="lg:col-span-3 flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-md bg-white border border-slate-200 p-8"
        >
          <KlysticLogo className="lg:hidden mb-6" label="UAT · Tracker" />
          <div className="overline text-klystic-sky">Welcome back</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-klystic-ink mt-1">
            Sign in
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Use your Klystic credentials and your 6-digit authenticator code.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label className="overline text-slate-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
              />
            </div>
            <div>
              <Label className="overline text-slate-500">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
              />
            </div>
            <div>
              <Label className="overline text-slate-500 flex items-center gap-2">
                <Smartphone size={12} /> Authenticator code
              </Label>
              <Input
                data-testid="login-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                className="rounded-none border-slate-300 font-mono text-center text-2xl tracking-[0.5em] focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy || code.length !== 6}
            data-testid="login-submit"
            className="w-full mt-6 bg-klystic-ink hover:bg-klystic-ink-dark text-white rounded-none h-11"
          >
            <LogIn size={16} className="mr-2" />
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="mt-6 pt-6 border-t border-slate-100 text-sm text-slate-500 text-center space-y-2">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 whitespace-nowrap">
              <Link
                to="/reset-password"
                data-testid="login-to-reset-password"
                className="text-klystic-sky font-medium hover:text-klystic-sky-dark"
              >
                Forgot password?
              </Link>
              <span className="text-slate-300">·</span>
              <Link
                to="/reset-2fa"
                data-testid="login-to-reset"
                className="text-klystic-sky font-medium hover:text-klystic-sky-dark"
              >
                Lost authenticator?
              </Link>
            </div>
            <div>
              No account yet?{" "}
              <Link
                to="/signup"
                data-testid="login-to-signup"
                className="text-klystic-sky font-medium hover:text-klystic-sky-dark"
              >
                Create one
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
